import { Campus } from '@/modules/core/data/types/Campus';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import { StudentSolutionGroup } from '@/modules/core/data/types/StudentSolutionGroup';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { StudentSolutionsGroupByType } from '@/modules/core/data/types/StudentSolutionsGroupByType';
import { createLogger } from '@/modules/core/logging/logger';
import * as Sentry from '@sentry/node';
import cloneDeep from 'lodash.clonedeep';
import find from 'lodash.find';
import findIndex from 'lodash.findindex';
import get from 'lodash.get';
import includes from 'lodash.includes';
import map from 'lodash.map';
import size from 'lodash.size';
import uniqBy from 'lodash.uniqby';
import { GenericRecord } from '../core/data/record';
import {
  CRITERIA_CAMPUSES_SOME_IS_IN_KEY,
  CRITERIA_CATEGORIES_SOME_IS_IN_KEY,
  CRITERIA_EDUCATIONAL_PROGRAM_STEPS_SOME_IS_IN_KEY,
  findByStudentSolutionEnhancedCriteriaRule,
} from './studentSolution';
import { filterUnusedContextEducationalPrograms } from './studentSolutionContext';

const fileLabel = 'utils/studentSolutionGroup';
const logger = createLogger({
  fileLabel,
});

/**
 * Transform a given array of solutions into arrays of sub-groups based on a given groupedBy predicate.
 *
 * XXX This function is so complex, with so many variations that each variation is unit tested in its own file
 *
 * @param {StudentSolution[]} studentSolutions
 * @param {StudentSolutionContext} context
 * @param {string} groupBy
 * @param {Campus[]} allCampuses
 * @param defaultGroupLabel
 * @param addDefaultGroup
 * @param forceGroupProgramByDisplayPriorityScore
 * @return {StudentSolutionGroup[]}
 */
export const computeStudentSolutionGroups = (studentSolutions: StudentSolution[], context: StudentSolutionContext, groupBy: StudentSolutionsGroupByType, allCampuses: Campus[], defaultGroupLabel: string, addDefaultGroup = true, forceGroupProgramByDisplayPriorityScore = false): StudentSolutionGroup[] => {
  // Default group that handles cases where no predicates are found for the current student solution (no category, no program, no campus, etc.)
  // XXX Isn't supposed to happen in production, but it might (there is no rule forcing customer to assign a category to every student solution, for instance)
  //  Adding items to a default group ensures the solution will be displayed to the end user
  const defaultGroup: StudentSolutionGroup = {
    id: 'default',
    ref: 'default',
    label: defaultGroupLabel,
    solutions: [],
    displayPriorityScore: -1, // Display those at last
    isDefaultGroup: true,
    __typename: groupBy,
  };
  const studentSolutionGroups: StudentSolutionGroup[] = [
    defaultGroup, // Automatically adds the default group (as fallback), even if it's not always used
  ];

  /**
   * Add a student solution to a group.
   *
   * Use the generic record to resolve to which group the solution belongs to
   * Creates the group if it doesn't exist already
   *
   * @param {StudentSolution} studentSolution
   * @param {T} record
   * @param {string} groupIdKey
   * @param {string} groupNameKey
   * @param {string} groupLabelKey
   * @param {string} groupDisplayPriorityScoreKey
   * @param {boolean} forceGroupProgramByDisplayPriorityScore
   */
  const _addStudentSolutionToGroup = <T extends GenericRecord>(studentSolution: StudentSolution, record: T, groupIdKey = 'id', groupNameKey = 'ref', groupLabelKey = 'label', groupDisplayPriorityScoreKey = 'displayPriorityScore', forceGroupProgramByDisplayPriorityScore = false): void => {
    const groupId: string = get(record, groupIdKey);
    const groupRef: string = get(record, groupNameKey);
    const groupLabel: string = get(record, groupLabelKey);
    let groupDisplayPriorityScore: number = get(record, groupDisplayPriorityScoreKey, 0);
    let group: StudentSolutionGroup = find(studentSolutionGroups, { id: groupId });

    if (!group) {
      // When ordering by program, we want to display the groups in the same order as they have been selected
      // (because we want to display the selected groups (campuses, programs, categories) in the /solutions pages in the same order as they've been selected),
      // unless specified otherwise (such as in SLS where we want to display the groups (campuses, programs) in the order defined by the customer.
      if (groupBy === 'EducationalProgram' && !forceGroupProgramByDisplayPriorityScore) {
        const educationalProgramIndex = findIndex(context.educationalPrograms, { id: groupId });
        groupDisplayPriorityScore = size(context.educationalPrograms) - educationalProgramIndex; // Reverse (lower index means higher display order priority)
      }

      if (groupId && groupRef && groupLabel) {
        group = {
          id: groupId,
          ref: groupRef,
          label: groupLabel,
          solutions: [],
          displayPriorityScore: groupDisplayPriorityScore,
          isDefaultGroup: false,
          __typename: groupBy,
        };

        studentSolutionGroups.push(group);
      } else {
        // This can happen when a published solution is assigned to a published step, but the step's program is not published (draft)
        // See https://app.asana.com/0/584852631485917/1201994859733892
        logger.warn(
          `Unable to add student solution "${studentSolution.ref}" to group because group is not defined (id|ref|label not defined)`,
          record,
          groupIdKey,
          groupNameKey,
          groupLabelKey,
        );
      }
    }

    if (group?.solutions) {
      group.solutions?.push(studentSolution);
    }
  };

  map(studentSolutions, (studentSolution: StudentSolution) => {
    if (groupBy === 'Category') {
      const categories: StudentSolutionsCategory[] = findByStudentSolutionEnhancedCriteriaRule(studentSolution, CRITERIA_CATEGORIES_SOME_IS_IN_KEY, []);

      if (size(categories)) {
        map(categories, (category: StudentSolutionsCategory) => {
          _addStudentSolutionToGroup(studentSolution, category);
        });
      } else {
        // Edge case, when no category is assigned to the solution
        const group: StudentSolutionGroup = find(studentSolutionGroups, { label: defaultGroup.label });
        group.solutions.push(studentSolution);
      }
    } else if (groupBy === 'Campus') {
      const campuses: Campus[] = findByStudentSolutionEnhancedCriteriaRule(studentSolution, CRITERIA_CAMPUSES_SOME_IS_IN_KEY, []);

      if (size(campuses)) {
        map(campuses, (campus: Campus) => {
          _addStudentSolutionToGroup(studentSolution, campus);
        });
      } else {
        // XXX Edge case, when no campus is assigned to the solution, then it means the solution is available on all campuses
        map(allCampuses, (campus: Campus) => {
          _addStudentSolutionToGroup(studentSolution, campus);
        });
      }

    } else if (groupBy === 'EducationalProgram') {
      const educationalProgramSteps: EducationalProgramStep[] = findByStudentSolutionEnhancedCriteriaRule(studentSolution, CRITERIA_EDUCATIONAL_PROGRAM_STEPS_SOME_IS_IN_KEY, []);

      if (size(educationalProgramSteps)) {
        map(educationalProgramSteps, (step: EducationalProgramStep) => {
          _addStudentSolutionToGroup(studentSolution, step, 'program.id', 'program.ref', 'program.label', 'program.displayPriorityScore', forceGroupProgramByDisplayPriorityScore);
        });
      } else {
        // Edge case, when no program step is assigned to the solution
        const group: StudentSolutionGroup = find(studentSolutionGroups, { label: defaultGroup.label });
        group.solutions.push(studentSolution);
      }
    } else {
      const message = `"groupedBy" predicate isn't supported, with value: "${groupBy}".`;
      logger.error(message);
      Sentry.captureException(new Error(message));
    }
  });

  // Clean up duplicate, per group
  map(studentSolutionGroups, (group: StudentSolutionGroup, index: number) => {
    studentSolutionGroups[index] = {
      ...group,
      solutions: uniqBy(group.solutions, 'id'),
    };
  });

  if (!addDefaultGroup) {
    studentSolutionGroups.shift(); // Remove default group that is always at index 0, because it's added first
  }

  return studentSolutionGroups;
};

/**
 * Resolve the default "groupBy" to use
 *
 * We order by "Category" by default, except if the user has selected multiple steps (that don't belong to the same program)
 * In order to know how many used educationalPrograms are really used, we filter out those that aren't (eliminating "ghost" programs)
 *
 * @param {StudentSolutionContext} studentSolutionContext
 * @return {StudentSolutionsGroupByType}
 */
export const resolveDefaultGroupBy = (studentSolutionContext: StudentSolutionContext): StudentSolutionsGroupByType => {
  // XXX Clone to avoid mutation, as it would have ugly side effects across the whole app
  const clonedStudentSolutionContext: StudentSolutionContext = cloneDeep(studentSolutionContext);
  const filteredStudentSolutionContext = filterUnusedContextEducationalPrograms(clonedStudentSolutionContext);

  if (size(filteredStudentSolutionContext.educationalPrograms) > 1) {
    return 'EducationalProgram';
  } else {
    return 'Category';
  }
};

/**
 * Filtering studentSolution groups based on the selected context filters and the current active filter ("current active filter" is the applied "orderBy", in this scenario)
 *
 * @param {StudentSolutionGroup[]} studentSolutionGroups
 * @param {StudentSolutionContext} context
 * @param {StudentSolutionsGroupByType} groupedBy
 * @return {StudentSolutionGroup[]}
 */
export const filterStudentSolutionGroups = (studentSolutionGroups: StudentSolutionGroup[], context: StudentSolutionContext, groupedBy: StudentSolutionsGroupByType): StudentSolutionGroup[] => {
  const studentSolutionDefaultGroup: StudentSolutionGroup = find(studentSolutionGroups, { isDefaultGroup: true });
  let filteredStudentSolutionGroups: StudentSolutionGroup[] = [];
  let activeContextFilters: Array<{ id: string }>;
  let minActiveContextFilters = 0; // Minimum of active context filters

  // XXX Clone to avoid mutation, as it would have ugly side effects across the whole app
  if (groupedBy === 'Campus') {
    activeContextFilters = cloneDeep(context?.campuses || []); // TODO Cloning might affect performances
  } else if (groupedBy === 'EducationalProgram') {
    activeContextFilters = cloneDeep(context?.educationalPrograms || []); // TODO Cloning might affect performances
  } else if (groupedBy === 'Category') {
    activeContextFilters = cloneDeep(context?.categories || []); // TODO Cloning might affect performances
  } else {
    const message = `Unexpected groupBy "${groupedBy}" used, was expecting a valid "StudentSolutionsGroupByType" value - Ignoring groupBy`;
    logger.warn(message);
    Sentry.captureMessage(message);
  }

  // Show the default group if it exists
  if (studentSolutionDefaultGroup) {
    activeContextFilters.push(studentSolutionDefaultGroup);

    // Increase it, as we'll have at least the default group as "active context filter",
    // even though it's not really a filter and must not be accounted for when counting active filters
    minActiveContextFilters += 1;
  }

  if (size(activeContextFilters) > minActiveContextFilters) {
    map(studentSolutionGroups, (studentSolutionGroup: StudentSolutionGroup) => {
      const allowedIds: string[] = map(activeContextFilters, 'id');

      if (includes(allowedIds, studentSolutionGroup.id)) {
        filteredStudentSolutionGroups.push(studentSolutionGroup);
      }
    });
  } else {
    // If there is no value in the context filter then it means there is no filter enabled, in such case we don't filter anything
    filteredStudentSolutionGroups = studentSolutionGroups;
  }

  return filteredStudentSolutionGroups;
};

/**
 * Gets the label to use for the default group, based on the type of groupBy being provided.
 *
 * @param groupBy
 */
export const getDefaultLabelByGroup = (groupBy: StudentSolutionsGroupByType): string => {
  if (groupBy === 'Category') {
    return 'Aucune';
  } else if (groupBy === 'EducationalProgram' || groupBy === 'Campus') {
    return 'Tous';
  } else {
    return 'Aucun';
  }
};
